/**
 * AgencyBulkRunTab.jsx
 * ------------------------------------------------------------------
 * Multi-select clients → fire bulk scan OR bulk GEO generation.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Loader2, Search, Sparkles, CheckSquare, Square, Play,
  AlertCircle, ChevronRight,
} from "lucide-react";

export default function AgencyBulkRunTab({ userEmail, onBatchStarted }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [maxUrls, setMaxUrls] = useState(50);
  const [maxTargets, setMaxTargets] = useState(5);
  const [templates, setTemplates] = useState(["product"]);
  const [autoPublish, setAutoPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userEmail) return;
    axios.get(`${API}/agency/clients`, {
      headers: { "X-User-Email": userEmail },
    }).then(r => setClients(r.data?.clients || [])).catch(() => {});
  }, [userEmail]);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelected(selected.length === clients.length ? [] : clients.map(c => c.id));
  };
  const toggleTemplate = (t) => {
    setTemplates(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const dispatch = async (kind) => {
    if (selected.length === 0) return;
    setSubmitting(true); setError(null); setLastBatch(null);
    try {
      const url = kind === "scan" ? "/agency/bulk-scan" : "/agency/bulk-geo";
      const payload = kind === "scan"
        ? { client_ids: selected, max_urls: maxUrls }
        : { client_ids: selected, max_targets_per_client: maxTargets, template_types: templates, auto_publish: autoPublish };
      const r = await axios.post(`${API}${url}`, payload, {
        headers: { "X-User-Email": userEmail },
      });
      setLastBatch(r.data);
      onBatchStarted?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.message || "Dispatch zlyhal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="agency-bulkrun-tab">
      {/* Client multi-select */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold">1. Vyber klientov ({selected.length} / {clients.length})</h3>
          <button
            onClick={toggleAll}
            data-testid="agency-bulk-toggle-all"
            className="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded-sm inline-flex items-center gap-1"
          >
            {selected.length === clients.length ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
            {selected.length === clients.length ? "Zrušiť výber" : "Označiť všetkých"}
          </button>
        </div>
        {clients.length === 0 ? (
          <div className="text-gray-500 text-sm">Žiadni klienti. Pridaj cez tab Klienti.</div>
        ) : (
          <div className="max-h-[260px] overflow-y-auto border border-gray-900 rounded-sm">
            {clients.map((c, i) => (
              <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-black/40 cursor-pointer border-b border-gray-900">
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={() => toggle(c.id)}
                  data-testid={`agency-bulk-client-${i}`}
                  className="accent-purple-500"
                />
                <span className="text-sm text-white flex-1 truncate">{c.name}</span>
                <span className="text-xs text-purple-400 truncate max-w-[200px]">{c.domain.replace(/^https?:\/\//, "")}</span>
                <span className="text-[10px] text-gray-500 uppercase">{c.language}</span>
              </label>
            ))}
          </div>
        )}
      </section>

      {/* Bulk Scan card */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="agency-bulk-scan-card">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-bold">Bulk Scan</h3>
        </div>
        <label className="block text-xs text-gray-400 mb-2">
          Max URLs na klienta: <span className="text-purple-300 font-bold tabular-nums">{maxUrls}</span>
        </label>
        <input
          type="range" min={10} max={500} step={10}
          value={maxUrls} onChange={(e) => setMaxUrls(Number(e.target.value))}
          data-testid="agency-bulk-scan-maxurls"
          className="w-full accent-purple-500 mb-3"
        />
        <div className="text-[10px] text-gray-500 mb-3">
          Spolu ≈ <b className="text-white">{selected.length * maxUrls}</b> URL credits zaberie tento batch
        </div>
        <button
          onClick={() => dispatch("scan")}
          disabled={submitting || selected.length === 0}
          data-testid="agency-bulk-scan-btn"
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Spustiť bulk scan
        </button>
      </section>

      {/* Bulk GEO card */}
      <section className="p-5 bg-gradient-to-br from-purple-900/20 to-black border border-purple-700/40 rounded-sm" data-testid="agency-bulk-geo-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-bold">Bulk GEO Pages</h3>
        </div>
        <label className="block text-xs text-gray-400 mb-2">
          Targets per klient: <span className="text-purple-300 font-bold tabular-nums">{maxTargets}</span>
        </label>
        <input
          type="range" min={1} max={20} step={1}
          value={maxTargets} onChange={(e) => setMaxTargets(Number(e.target.value))}
          data-testid="agency-bulk-geo-maxtargets"
          className="w-full accent-purple-500 mb-3"
        />
        <div className="flex flex-wrap gap-2 mb-3">
          {["product", "category", "faq"].map(t => (
            <button
              key={t}
              onClick={() => toggleTemplate(t)}
              data-testid={`agency-bulk-geo-template-${t}`}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm border ${
                templates.includes(t)
                  ? "bg-purple-900/40 border-purple-500 text-purple-100"
                  : "bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-300 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={autoPublish}
            onChange={(e) => setAutoPublish(e.target.checked)}
            data-testid="agency-bulk-geo-autopublish"
            className="accent-purple-500"
          />
          Auto-publikovať na <span className="text-purple-300">/api/geo-page/&lt;klient&gt;/&lt;slug&gt;</span>
        </label>
        <div className="text-[10px] text-gray-500 mb-3">
          Spolu ≈ <b className="text-white">{selected.length * maxTargets * 3 * templates.length}</b> page credits (5 SK miest defaultne × šablóny)
        </div>
        <button
          onClick={() => dispatch("geo")}
          disabled={submitting || selected.length === 0 || templates.length === 0}
          data-testid="agency-bulk-geo-btn"
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Spustiť bulk GEO
        </button>
      </section>

      {error && (
        <div className="p-3 bg-rose-900/20 border border-rose-700/40 rounded-sm text-sm text-rose-200 flex items-start gap-2" data-testid="agency-bulk-error">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}

      {lastBatch && (
        <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-sm" data-testid="agency-bulk-last-batch">
          <div className="text-sm font-bold mb-1">
            ✓ Batch spustený: <code className="text-emerald-300 text-xs">{lastBatch.id}</code>
          </div>
          <div className="text-xs text-gray-300 mb-2">
            Typ: <b>{lastBatch.kind}</b> · {lastBatch.total} klientov · status: {lastBatch.status}
          </div>
          <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="agency-tab-batches"]')?.click(); }} className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1">
            Sleduj progress v Job log <ChevronRight className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}
